/**
 * DashboardPage — live Vedic astrology dashboard.
 * Phase 5: Asymmetric layout with MuhurtaTimeline + QuickForecast.
 */
import { useQuery } from '@tanstack/react-query'
import { dashboardApi, gocharApi, choghadiyaApi, type GocharTransit } from '@/lib/api'
import { useUser } from '@/store'
import { cn } from '@/lib/utils'
import { Sun, Moon, TrendingUp, RefreshCw, ArrowUpRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { SkeletonDashboard, ErrorState } from '@/components/ui'
import { useStaggerEntrance } from '@/hooks/useStaggerEntrance'
import { useMicroInteraction } from '@/hooks/useMicroInteraction'
import { MuhurtaTimeline } from '@/components/dashboard/MuhurtaTimeline'
import { QuickForecast } from '@/components/dashboard/QuickForecast'
import { Card } from '@/components/Card'
import { Button } from '@/components/Button'

// ── Gauge ring (SVG) ──────────────────────────────────────────────────────────

function AuspiciousnessGauge({ score }: { score: number }) {
  const size        = 160
  const strokeWidth = 12
  const radius      = (size - strokeWidth * 2) / 2
  const circumference = 2 * Math.PI * radius
  const arcFraction = 0.75
  const dashTotal   = circumference * arcFraction
  const dashOffset  = dashTotal * (1 - score / 100)

  const color     = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : score >= 30 ? '#eab308' : '#ef4444'
  const colorGlow = score >= 75 ? 'rgba(34,197,94,0.35)' : score >= 50 ? 'rgba(245,158,11,0.35)' : score >= 30 ? 'rgba(234,179,8,0.35)' : 'rgba(239,68,68,0.35)'
  const label     = score >= 75 ? 'Excellent' : score >= 50 ? 'Good' : score >= 30 ? 'Fair' : 'Challenging'

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
          <defs>
            <filter id="gauge-glow">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>
          {/* Track */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none"
            style={{ stroke: 'var(--chart-border)' }}
            strokeWidth={strokeWidth}
            strokeDasharray={`${dashTotal} ${circumference - dashTotal}`}
            strokeLinecap="round"
            transform={`rotate(135 ${size / 2} ${size / 2})`}
          />
          {/* Glow halo */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth + 4}
            strokeDasharray={`${dashTotal - dashOffset} ${circumference}`}
            strokeLinecap="round"
            transform={`rotate(135 ${size / 2} ${size / 2})`}
            opacity={0.18}
            filter="url(#gauge-glow)"
          />
          {/* Fill */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${dashTotal - dashOffset} ${circumference}`}
            strokeLinecap="round"
            transform={`rotate(135 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.16, 1, 0.3, 1)' }}
          />
          {/* Score */}
          <text x={size / 2} y={size / 2 - 6} textAnchor="middle" fill={color}
            fontSize={32} fontWeight={700} fontFamily="Geist, sans-serif">
            {score}
          </text>
          <text x={size / 2} y={size / 2 + 14} textAnchor="middle" style={{ fill: 'var(--chart-text-muted)' }}
            fontSize={11} fontFamily="Geist Mono, monospace">
            / 100
          </text>
        </svg>
        {/* Outer glow ring */}
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ boxShadow: `0 0 40px ${colorGlow}` }}
          aria-hidden="true"
        />
      </div>
      <div className="text-center">
        <span className="text-sm font-semibold" style={{ color }}>{label}</span>
        <p className="text-star-dim text-2xs font-mono mt-0.5 uppercase tracking-wider">Auspiciousness</p>
      </div>
    </div>
  )
}

// ── Transit badge ─────────────────────────────────────────────────────────────

function TransitBadge({ transit }: { transit: GocharTransit }) {
  const { planet, event, impact, description, exactDate } = transit
  const cls = impact === 'positive'
    ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    : impact === 'negative'
    ? 'text-red-400 bg-red-500/10 border-red-500/20'
    : 'text-amber-400 bg-amber-500/10 border-amber-500/20'
  return (
    <div className={cn('px-3 py-2 rounded-lg border text-xs space-y-0.5', cls)}>
      <div className="flex items-center gap-2">
        <span className="font-semibold">{planet}</span>
        <span className="opacity-80">{event}</span>
        <span className="ml-auto font-mono tabular-nums opacity-60">{dayjs(exactDate).format('MMM D')}</span>
      </div>
      {description && (
        <p className="opacity-70 leading-snug">{description}</p>
      )}
    </div>
  )
}

const PLANET_COLORS: Record<string, string> = {
  Sun:     'text-amber-400',
  Moon:    'text-zinc-300',
  Mars:    'text-red-400',
  Mercury: 'text-green-400',
  Jupiter: 'text-yellow-400',
  Venus:   'text-pink-400',
  Saturn:  'text-slate-400',
  Rahu:    'text-violet-400',
  Ketu:    'text-orange-400',
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const user  = useUser()
  const today = dayjs()

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.get(),
    retry: 1,
  })

  const { data: gocharData } = useQuery({
    queryKey: ['gochar', user?.id],
    queryFn: () => gocharApi.getTransits(user!.id),
    enabled: !!user?.id,
    retry: 1,
  })

  const { data: dailyTimingsData } = useQuery({
    queryKey: ['daily-timings', today.format('YYYY-MM-DD')],
    queryFn: () => choghadiyaApi.getDailyTimings({
      date: today.format('YYYY-MM-DD'),
      lat:  28.6139,
      lon:  77.209,
      tz:   user?.profile?.birthTimezone ?? 'Asia/Kolkata',
    }),
    retry: 1,
  })

  const topRowRef      = useStaggerEntrance<HTMLDivElement>({ stagger: 0.07, duration: 0.45 })
  const sectionsRef    = useStaggerEntrance<HTMLDivElement>({ selector: ':scope > div', stagger: 0.08, duration: 0.4, delay: 0.2 })
  const quickActionsRef = useStaggerEntrance<HTMLDivElement>({ stagger: 0.06, duration: 0.35, delay: 0.35 })
  const refreshBtn     = useMicroInteraction<HTMLButtonElement>()

  if (isLoading) return <SkeletonDashboard className="max-w-6xl" />
  if (isError || !data) return <ErrorState message="Unable to load dashboard data. Please try again." onRetry={refetch} />

  const { panchang, activeDasha } = data
  const todayMuhurta        = Array.isArray(data.todayMuhurta) ? data.todayMuhurta : []
  const transitAlerts: GocharTransit[] = Array.isArray(gocharData) ? gocharData : []
  const inauspiciousPeriods = Array.isArray(panchang?.inauspiciousPeriods) ? panchang.inauspiciousPeriods : []
  const choghadiyaSlots     = Array.isArray(dailyTimingsData?.choghadiya) ? dailyTimingsData.choghadiya : []

  return (
    <div className="max-w-6xl space-y-6">

      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="heading-cosmic text-xl font-semibold">
            {user?.displayName ? `${user.displayName}'s Dashboard` : 'Your Dashboard'}
          </h1>
          <p className="text-star-muted text-sm mt-0.5 font-mono">
            {today.format('dddd, MMMM D YYYY')}
            <span className="text-star-dim mx-1.5">·</span>
            Live guidance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cosmic-dusk border border-cosmic-mist">
            <Sun className="w-3.5 h-3.5 text-amber-400" aria-hidden />
            <span className="text-star-soft text-2xs font-mono tabular-nums">{panchang.sunriseTime}</span>
            <span className="text-star-dim text-2xs mx-1">·</span>
            <Moon className="w-3.5 h-3.5 text-violet-glow" aria-hidden />
            <span className="text-star-soft text-2xs font-mono tabular-nums">{panchang.sunsetTime}</span>
          </div>
          <Button
            ref={refreshBtn.ref}
            onMouseDown={refreshBtn.onMouseDown}
            onMouseUp={refreshBtn.onMouseUp}
            onMouseLeave={refreshBtn.onMouseLeave}
            onClick={() => refetch()}
            disabled={isLoading}
            variant="ghost"
            size="sm"
            aria-label="Refresh"
            className="p-2"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isLoading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* ── Top row: Gauge + Dashas + Transits ── */}
      <div ref={topRowRef} className="grid lg:grid-cols-3 gap-4">

        {/* Auspiciousness gauge + Panchang */}
        <Card className="p-5 flex flex-col items-center gap-5">
          <AuspiciousnessGauge score={panchang.auspiciousnessScore} />
          <div className="w-full grid grid-cols-2 gap-2 text-center">
            {[
              { label: 'Tithi',     value: `${panchang.tithi} (${panchang.paksha})` },
              { label: 'Nakshatra', value: panchang.nakshatra },
              { label: 'Yoga',      value: panchang.yoga },
              { label: 'Karana',    value: panchang.karana },
            ].map(({ label, value }) => (
              <div key={label} className="bg-cosmic-dusk/60 rounded-xl p-2.5 border border-cosmic-mist/50">
                <p className="overline">{label}</p>
                <p className="text-star-soft text-xs font-medium mt-1 truncate">{value}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Active dashas */}
        <Card className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="heading-4">Active Dashas</h2>
            <Link to="/dasha" className="text-gold/70 text-2xs hover:text-gold transition-colors flex items-center gap-1">
              Timeline <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {[
              { node: activeDasha.mahadasha,       label: 'Mahadasha' },
              { node: activeDasha.antardasha,      label: 'Antardasha' },
              { node: activeDasha.pratyantardasha, label: 'Pratyantardasha' },
            ].filter(({ node }) => node !== null).map(({ node, label }) => {
              if (!node) return null
              return (
                <div key={label} className="gold-accent pl-3">
                  <p className={cn('text-sm font-semibold', PLANET_COLORS[node.planet] ?? 'text-star-bright')}>
                    {node.planet}
                  </p>
                  <p className="text-star-dim text-2xs font-mono mt-0.5">
                    {label} · until {dayjs(node.endDate).format('MMM YYYY')}
                  </p>
                </div>
              )
            })}
          </div>
          <div className="pt-3 border-t border-cosmic-mist/40">
            <p className="overline mb-2">Daily Guidance</p>
            <p className="text-star-soft text-sm leading-relaxed font-display italic">
              "{activeDasha.narrative}"
            </p>
          </div>
        </Card>

        {/* Transit alerts */}
        <Card className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="heading-4">Today's Transits</h2>
            <TrendingUp className="w-4 h-4 text-star-dim" aria-hidden />
          </div>
          <div className="space-y-2">
            {transitAlerts.length === 0
              ? <p className="text-star-dim text-xs font-mono">No active transits</p>
              : transitAlerts.map((t, i) => <TransitBadge key={i} transit={t} />)
            }
          </div>
        </Card>
      </div>

      {/* ── Asymmetric main section: 2/3 Muhurta Clock + 1/3 Quick Forecast ── */}
      <div ref={sectionsRef} className="grid lg:grid-cols-3 gap-4">

        {/* Muhurta Clock — 2/3 */}
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="heading-4">
                Muhurta Clock
                <span className="text-star-dim text-2xs font-display ml-1.5">मुहूर्त</span>
              </h2>
              <p className="caption mt-0.5">Today's auspicious windows</p>
            </div>
            <Link to="/muhurta" className="text-gold/70 text-2xs hover:text-gold transition-colors flex items-center gap-1">
              Full calendar <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <MuhurtaTimeline slots={todayMuhurta} />
        </Card>

        {/* Quick Forecast — 1/3 */}
        <Card className="p-5">
          <h2 className="heading-4 mb-3">
            Quick Forecast
            <span className="text-star-dim text-2xs font-display ml-1.5">चौघड़िया</span>
          </h2>
          <QuickForecast
            choghadiyaSlots={choghadiyaSlots}
            inauspiciousPeriods={inauspiciousPeriods}
            narrative={activeDasha.narrative}
          />
        </Card>
      </div>

      {/* ── Quick actions ── */}
      <div ref={quickActionsRef} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { to: '/muhurta',       emoji: '📅', label: 'Find Muhurta',  desc: 'Auspicious timing' },
          { to: '/compatibility', emoji: '💫', label: 'Kundali Match', desc: 'Compatibility' },
          { to: '/insights',      emoji: '🔮', label: 'Influences',    desc: '6-area analysis' },
          { to: '/remedies',      emoji: '✨', label: 'Remedies',      desc: 'Planetary solutions' },
        ].map(({ to, emoji, label, desc }) => (
          <Link
            key={to}
            to={to}
            className="bg-cosmic-nebula/50 ring-1 ring-white/5 rounded-2xl backdrop-blur p-4 flex flex-col gap-3 group
                       hover:bg-cosmic-nebula/70 hover:ring-gold/10 hover:shadow-card-hover transition-all duration-200"
          >
            <span className="text-2xl">{emoji}</span>
            <div>
              <p className="text-star-soft text-sm font-medium group-hover:text-gold transition-colors duration-fast">{label}</p>
              <p className="caption mt-0.5">{desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
